"use client";

import {
  type InfiniteData,
  type UseQueryResult,
  useQueries,
  useQueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@clerk/nextjs";
import { useMemo, useState } from "react";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { QUERIES } from "modules/common/query";
import type {
  ActiveTradeReference,
  TradeItem,
  TradeItemAvailability,
  TradeItemAvailabilityResponse,
} from "modules/tradeLists/model/tradeList";
import { TradeListType } from "modules/tradeLists/model/tradeList";
import type { TradeBrowseQueryRequest } from "modules/tradeLists/utils/buildBrowseQueryParams";
import {
  canonicalizeAvailabilityGroups,
  canonicalizeAvailabilityGroupsByPage,
  chunkAvailabilityGroups,
  type TradeItemAvailabilityBatchRequest,
  type TradeItemAvailabilityGroup,
} from "modules/tradeLists/utils/tradeItemAvailabilityBatch";
import { tradeItemGroupKey } from "modules/tradeLists/utils/tradeItemGroupKey";
import { API_ROUTES } from "utils/api-route.constants";

type AvailabilityOverlayItem = Pick<TradeItem, "ids"> &
  Partial<
    Pick<
      TradeItem,
      "availableIds" | "spokenForQuantity" | "activityStatus" | "activeTrades"
    >
  >;

type BrowsePage<TItem extends AvailabilityOverlayItem = TradeItem> = {
  items: TItem[];
};

interface InfiniteAvailabilityOptions {
  retainCachedExactGroups?: boolean;
}

const AVAILABILITY_QUERY_CONFIG = {
  staleTime: 30 * 1000,
  gcTime: 5 * 60 * 1000,
  refetchInterval: false as const,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  refetchOnMount: true,
};

type AvailabilitySnapshot = {
  availabilityByGroup: ReadonlyMap<string, TradeItemAvailability>;
  requestedGroupKeys: ReadonlySet<string>;
  resolvedGroupKeys: ReadonlySet<string>;
  shouldFetch: boolean;
};

type AvailabilityQuerySnapshot = Pick<
  UseQueryResult<TradeItemAvailabilityResponse>,
  "data" | "isSuccess"
>;

function combineAvailabilityQueries(
  results: UseQueryResult<TradeItemAvailabilityResponse>[]
): AvailabilityQuerySnapshot[] {
  return results.map(({ data, isSuccess }) => ({ data, isSuccess }));
}

// Infinite-query pages and their unchanged item objects are structurally
// shared by React Query. Cache both levels: one reconciled row can replace its
// page object, but unchanged siblings should retain their rendered identity.
type OverlayVariant = { signature: string; value: unknown };
type OverlayVariants = {
  pages: WeakMap<object, OverlayVariant>;
  items: WeakMap<object, OverlayVariant>;
};

function useOverlayVariants(): OverlayVariants {
  const [variants] = useState<OverlayVariants>(() => ({
    pages: new WeakMap<object, OverlayVariant>(),
    items: new WeakMap<object, OverlayVariant>(),
  }));
  return variants;
}

function canonicalActiveTradeReferences(
  activeTrades: readonly ActiveTradeReference[] | undefined
): ActiveTradeReference[] | undefined {
  if (!activeTrades?.length) return undefined;

  return [...activeTrades].sort((left, right) => {
    const chatOrder = left.chatId.localeCompare(right.chatId);
    if (chatOrder !== 0) return chatOrder;

    return JSON.stringify(left).localeCompare(JSON.stringify(right));
  });
}

/** Stable identity for the authorized destinations attached to one ID group. */
export function activeTradeReferencesSignature(
  activeTrades: readonly ActiveTradeReference[] | undefined
): string {
  return JSON.stringify(
    (canonicalActiveTradeReferences(activeTrades) ?? []).map((trade) => [
      trade.chatId,
      trade.quantity,
      trade.partner?.id ?? null,
      trade.partner?.username ?? null,
      trade.partner?.preferredAvatar ?? null,
      trade.status ?? null,
      trade.updatedAt ?? null,
    ])
  );
}

function idsSignature(ids: readonly string[] | undefined): string {
  return JSON.stringify(ids ?? []);
}

function resolvedAvailableIds<TItem extends AvailabilityOverlayItem>(
  item: TItem,
  live: TradeItemAvailability
): string[] {
  // During a rolling deployment an older API can omit the new field. Zero
  // reservations still makes the whole group safe; otherwise never guess
  // concrete IDs from only a count.
  return live.availableIds ?? (live.spokenForQuantity === 0 ? item.ids : []);
}

function overlayItemFromSnapshot<TItem extends AvailabilityOverlayItem>(
  item: TItem,
  snapshot: AvailabilitySnapshot
): TItem {
  const groupKey = tradeItemGroupKey(item.ids);
  if (!snapshot.requestedGroupKeys.has(groupKey)) return item;

  const live = snapshot.availabilityByGroup.get(groupKey);
  if (live) {
    const activeTrades = canonicalActiveTradeReferences(live.activeTrades);
    const availableIds = resolvedAvailableIds(item, live);
    if (
      idsSignature(item.availableIds) === idsSignature(availableIds) &&
      item.spokenForQuantity === live.spokenForQuantity &&
      item.activityStatus === live.activityStatus &&
      activeTradeReferencesSignature(item.activeTrades) ===
        activeTradeReferencesSignature(activeTrades)
    ) {
      return item;
    }

    return {
      ...item,
      availableIds,
      spokenForQuantity: live.spokenForQuantity,
      activityStatus: live.activityStatus,
      activeTrades,
    };
  }

  // A missing response row means zero only when the exact chunk containing
  // the group completed. Pending, failed, and skipped groups retain the
  // browse snapshot.
  if (!snapshot.resolvedGroupKeys.has(groupKey)) return item;
  if (
    idsSignature(item.availableIds) === idsSignature(item.ids) &&
    item.spokenForQuantity === 0 &&
    item.activityStatus === undefined &&
    !item.activeTrades?.length
  ) {
    return item;
  }

  return {
    ...item,
    availableIds: item.ids,
    spokenForQuantity: 0,
    activityStatus: undefined,
    activeTrades: undefined,
  };
}

function itemSnapshotSignature<TItem extends AvailabilityOverlayItem>(
  item: TItem,
  snapshot: AvailabilitySnapshot
): string {
  const groupKey = tradeItemGroupKey(item.ids);
  if (!snapshot.requestedGroupKeys.has(groupKey)) {
    return JSON.stringify([groupKey, "raw"]);
  }

  const live = snapshot.availabilityByGroup.get(groupKey);
  if (live) {
    return JSON.stringify([
      groupKey,
      "live",
      resolvedAvailableIds(item, live),
      live.spokenForQuantity,
      live.activityStatus === undefined
        ? ["undefined"]
        : ["value", live.activityStatus],
      activeTradeReferencesSignature(live.activeTrades),
    ]);
  }

  return JSON.stringify([
    groupKey,
    snapshot.resolvedGroupKeys.has(groupKey) ? "empty" : "pending",
  ]);
}

function overlayItemsFromSnapshot<TItem extends AvailabilityOverlayItem>(
  items: TItem[],
  snapshot: AvailabilitySnapshot,
  itemVariants?: WeakMap<object, OverlayVariant>
): TItem[] {
  let changed = false;
  const overlaidItems = items.map((item) => {
    const signature = itemSnapshotSignature(item, snapshot);
    const previous = itemVariants?.get(item);
    const overlaidItem =
      previous?.signature === signature
        ? (previous.value as TItem)
        : overlayItemFromSnapshot(item, snapshot);
    if (previous?.signature !== signature) {
      itemVariants?.set(item, { signature, value: overlaidItem });
    }
    if (overlaidItem !== item) changed = true;
    return overlaidItem;
  });

  return changed ? overlaidItems : items;
}

function pageSnapshotSignature<TItem extends AvailabilityOverlayItem>(
  items: TItem[],
  snapshot: AvailabilitySnapshot
): string {
  return JSON.stringify(
    items.map((item) => itemSnapshotSignature(item, snapshot))
  );
}

function overlayPageFromSnapshot<
  TItem extends AvailabilityOverlayItem,
  TPage extends BrowsePage<TItem>,
>(
  page: TPage,
  snapshot: AvailabilitySnapshot,
  variants: OverlayVariants
): TPage {
  const signature = pageSnapshotSignature(page.items, snapshot);
  const previous = variants.pages.get(page);
  if (previous?.signature === signature) return previous.value as TPage;

  const items = overlayItemsFromSnapshot(page.items, snapshot, variants.items);
  const overlaidPage = items === page.items ? page : { ...page, items };
  variants.pages.set(page, { signature, value: overlaidPage });
  return overlaidPage;
}

function overlayInfiniteDataFromSnapshot<
  TItem extends AvailabilityOverlayItem,
  TPage extends BrowsePage<TItem>,
>(
  data: InfiniteData<TPage>,
  snapshot: AvailabilitySnapshot,
  variants: OverlayVariants
) {
  const pages = data.pages.map((page) =>
    overlayPageFromSnapshot(page, snapshot, variants)
  );
  return pages.every((page, index) => page === data.pages[index])
    ? data
    : { ...data, pages };
}

export function overlayBrowseItemAvailability<
  TItem extends AvailabilityOverlayItem,
>(
  items: TItem[],
  availability: TradeItemAvailability[] | undefined,
  hasResolvedSnapshot = true,
  requestedGroupKeys?: ReadonlySet<string>,
  resolvedGroupKeys?: ReadonlySet<string>
): TItem[] {
  const availabilityByGroup = new Map(
    (availability ?? []).map((item) => [tradeItemGroupKey(item.ids), item])
  );
  const requested =
    requestedGroupKeys ??
    new Set(items.map((item) => tradeItemGroupKey(item.ids)));
  const resolved =
    resolvedGroupKeys ?? (hasResolvedSnapshot ? requested : new Set<string>());

  return overlayItemsFromSnapshot(items, {
    availabilityByGroup,
    requestedGroupKeys: requested,
    resolvedGroupKeys: resolved,
    shouldFetch: requested.size > 0,
  });
}

/**
 * A page's groups share one availability query key. If one group changes IDs,
 * the replacement query is pending for every unchanged sibling as well. Reuse
 * only exact rows from this viewer's still-live React Query entries; an exact
 * response for the replacement chunk always wins, including an omitted row
 * that authoritatively means zero reservations.
 */
export function mergePendingExactAvailability(
  availabilityByGroup: ReadonlyMap<string, TradeItemAvailability>,
  requestedGroupKeys: ReadonlySet<string>,
  resolvedGroupKeys: ReadonlySet<string>,
  cachedResponses: readonly TradeItemAvailabilityResponse[]
): Map<string, TradeItemAvailability> {
  const merged = new Map(availabilityByGroup);

  for (const response of cachedResponses) {
    for (const item of response.items) {
      const groupKey = tradeItemGroupKey(item.ids);
      if (
        requestedGroupKeys.has(groupKey) &&
        !resolvedGroupKeys.has(groupKey) &&
        !merged.has(groupKey)
      ) {
        merged.set(groupKey, item);
      }
    }
  }

  return merged;
}

function useItemAvailability(
  chunks: TradeItemAvailabilityGroup[][],
  enabled: boolean,
  retainCachedExactGroups = false
) {
  const { isLoaded: isAuthLoaded, userId } = useAuth();
  const fetch = useFetchWrapper();
  const queryClient = useQueryClient();
  const shouldFetch = enabled && isAuthLoaded && chunks.length > 0;
  // Activity status in this otherwise public response is viewer/privacy
  // dependent. Never let the pre-Clerk anonymous request populate a signed-in
  // observer, and never reuse one account's overlay after an account switch.
  const viewerKey = isAuthLoaded
    ? userId
      ? `user:${userId}`
      : "anonymous"
    : "auth-pending";
  const querySnapshots = useQueries({
    queries: chunks.map((groups) => ({
      // Keep this prefix stable: proposal mutations patch and invalidate every
      // active availability chunk through it. The remainder is derived only
      // from canonical requested ID groups.
      queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY, viewerKey, groups],
      queryFn: (): Promise<TradeItemAvailabilityResponse> => {
        const request: TradeItemAvailabilityBatchRequest = { groups };
        return fetch.post(
          API_ROUTES.TRADE.BROWSE_AVAILABILITY,
          request
        ) as Promise<TradeItemAvailabilityResponse>;
      },
      enabled: shouldFetch,
      ...AVAILABILITY_QUERY_CONFIG,
    })),
    // `useQueries` otherwise returns fresh tracked result wrappers on every
    // render. React Query structurally shares this bounded projection, keeping
    // the derived overlay snapshot stable when no relevant field changed.
    combine: combineAvailabilityQueries,
  });

  return useMemo<AvailabilitySnapshot>(() => {
    const availabilityByGroup = new Map<string, TradeItemAvailability>();
    const requestedGroupKeys = new Set<string>();
    const resolvedGroupKeys = new Set<string>();

    chunks.forEach((groups, index) => {
      const chunkGroupKeys = new Set<string>();
      for (const group of groups) {
        const groupKey = tradeItemGroupKey(group.ids);
        chunkGroupKeys.add(groupKey);
        requestedGroupKeys.add(groupKey);
      }

      const query = querySnapshots[index];
      for (const item of query?.data?.items ?? []) {
        const groupKey = tradeItemGroupKey(item.ids);
        // Accept only exact rows for this chunk so cache data cannot leak
        // across observer slots when the requested page set changes.
        if (chunkGroupKeys.has(groupKey)) {
          availabilityByGroup.set(groupKey, item);
        }
      }

      if (query?.isSuccess) {
        for (const groupKey of chunkGroupKeys) {
          resolvedGroupKeys.add(groupKey);
        }
      }
    });

    const retainedAvailabilityByGroup = retainCachedExactGroups
      ? mergePendingExactAvailability(
          availabilityByGroup,
          requestedGroupKeys,
          resolvedGroupKeys,
          queryClient
            .getQueriesData<TradeItemAvailabilityResponse>({
              queryKey: [QUERIES.TRADE_ITEM_AVAILABILITY, viewerKey],
            })
            .flatMap(([, data]) => (data ? [data] : []))
        )
      : availabilityByGroup;

    return {
      availabilityByGroup: retainedAvailabilityByGroup,
      requestedGroupKeys,
      resolvedGroupKeys,
      shouldFetch,
    };
  }, [
    chunks,
    queryClient,
    querySnapshots,
    retainCachedExactGroups,
    shouldFetch,
    viewerKey,
  ]);
}

export function useInfiniteBrowseItemAvailability<
  TItem extends AvailabilityOverlayItem,
  TPage extends BrowsePage<TItem>,
>(
  req: TradeBrowseQueryRequest,
  data: InfiniteData<TPage> | undefined,
  enabled = true,
  itemLimit?: number,
  options: InfiniteAvailabilityOptions = {}
): InfiniteData<TPage> | undefined {
  const isTradeList = (req.type ?? req.listType) === TradeListType.TRADES;
  const variants = useOverlayVariants();
  const chunks = useMemo(
    () =>
      canonicalizeAvailabilityGroupsByPage(
        data?.pages.map((page) => page.items) ?? [],
        itemLimit
      ).flatMap((groups) => chunkAvailabilityGroups(groups)),
    [data?.pages, itemLimit]
  );
  const snapshot = useItemAvailability(
    chunks,
    enabled && isTradeList,
    options.retainCachedExactGroups
  );

  // Each source page is memoized by only the availability state relevant to
  // its rows. Appending or resolving another page preserves existing objects.
  return useMemo(() => {
    if (!data || !snapshot.shouldFetch) return data;
    return overlayInfiniteDataFromSnapshot(data, snapshot, variants);
  }, [data, snapshot, variants]);
}

export function useBrowsePageItemAvailability<
  TItem extends AvailabilityOverlayItem,
  TPage extends BrowsePage<TItem>,
>(
  req: TradeBrowseQueryRequest,
  data: TPage | undefined,
  enabled = true,
  itemLimit?: number
): TPage | undefined {
  const isTradeList = (req.type ?? req.listType) === TradeListType.TRADES;
  const variants = useOverlayVariants();
  const groups = useMemo(
    () => canonicalizeAvailabilityGroups(data?.items ?? [], itemLimit),
    [data?.items, itemLimit]
  );
  const chunks = useMemo(() => chunkAvailabilityGroups(groups), [groups]);
  const snapshot = useItemAvailability(chunks, enabled && isTradeList);

  return useMemo(() => {
    if (!data || !snapshot.shouldFetch) return data;
    return overlayPageFromSnapshot(data, snapshot, variants);
  }, [data, snapshot, variants]);
}
